import { useMemo } from 'react'

import { NetworkStatus } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import type { useQuery as UseQuery } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'

import { buildTagComparator } from '~publish/components/FilterByTag'
import { useDebouncedTask } from '~publish/components/ContentItemCard/connectionRefresh'
import { ContentItemCard_ContentItem } from '~publish/components/ContentItemCard/fragments/ContentItemCard_ContentItem'
import { PostCard_Post } from '~publish/components/PostCard/fragments/PostCard_Post'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { isOfType } from '~publish/helpers/typeGuards'
import { CalendarPostCard_Post } from '~publish/pages/Calendar/fragments/CalendarPostCard_Post'
import { useInfiniteScrollPagination } from '~publish/hooks/useInfiniteScrollPagination'
import type { UseIntersectionObserverReturn } from '~publish/hooks/useIntersectionObserver'
import { PusherEvent, usePusherEvent } from '~publish/services/pusher'
import type { PostStatus } from './usePaginatedPostList'

export const CONTENTS_PER_PAGE = 50

// Module scope keeps the array identity stable, so the listeners bind once.
const CONTENT_ITEM_EVENTS = [
  PusherEvent.CONTENT_ITEM_CREATED,
  PusherEvent.CONTENT_ITEM_UPDATED,
  PusherEvent.CONTENT_ITEM_DELETED,
]

// `contents` covers every status, so no status lets this list skip an event.
const POST_EVENTS = [
  PusherEvent.POST_CREATED,
  PusherEvent.POST_UPDATED,
  PusherEvent.DRAFT_UPDATED,
  PusherEvent.DRAFT_MOVED,
  PusherEvent.DRAFT_APPROVED,
  PusherEvent.POST_TAG_ADDED,
  PusherEvent.POST_TAG_REMOVED,
]

// These change only a post's status. With no status or date filter, a post
// the list does not hold cannot join it, and a busy organization sends posts
// all day.
const POST_STATUS_EVENTS = [PusherEvent.POST_SENT, PusherEvent.POST_FAILED]

// A deleted post matters only when the list holds it.
const POST_REMOVED_EVENTS = [PusherEvent.POST_DELETED]

type PusherPostEventData = {
  update_id?: string
  draft_id?: string
  profile_id?: string
}

export const GetContentsList = graphql(
  `
    query GetContentsList(
      $first: Int!
      $after: String
      $organizationId: OrganizationId!
      $sort: [ContentsSortInput!]
      $channel: ContentsChannelFilter
      $tags: TagComparator
      $status: [PostStatus!]
      $date: ContentsDateFilter
    ) {
      contents(
        input: {
          organizationId: $organizationId
          sort: $sort
          filter: {
            channel: $channel
            tags: $tags
            status: $status
            date: $date
          }
        }
        first: $first
        after: $after
      ) {
        edges {
          cursor
          date
          # Aliased per branch — see the identical comment in
          # useDraftsList.ts's GetDraftsList for why.
          postNode: node {
            __typename
            ... on Post {
              id
              dueAt
              sentAt
              ...PostCard_Post
              ...CalendarPostCard_Post
            }
          }
          contentItemNode: node {
            __typename
            ... on ContentItem {
              id
              ...ContentItemCard_ContentItem
            }
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasPreviousPage
          hasNextPage
        }
        totalCount
      }
    }
  `,
  [PostCard_Post, CalendarPostCard_Post, ContentItemCard_ContentItem],
)

type GetContentsListData = ResultOf<typeof GetContentsList>
type GetContentsListVariables = VariablesOf<typeof GetContentsList>

export type ContentsSortInput = NonNullable<
  GetContentsListVariables['sort']
>[number]
export type ContentEdge = GetContentsListData['contents']['edges'][number]

/** The `range` branch of the one-of `filter.date` input. */
export type ContentsDateRange = Extract<
  NonNullable<GetContentsListVariables['date']>,
  { range: unknown }
>['range']

/** The edge's node id, whichever branch of the union actually matched. */
export function getContentEdgeId(edge: ContentEdge): string | undefined {
  if (isOfType(edge.postNode, 'Post')) return edge.postNode.id
  if (isOfType(edge.contentItemNode, 'ContentItem')) {
    return edge.contentItemNode.id
  }
  return undefined
}

// Newest first, the order the other Board columns use.
const DEFAULT_SORT: ContentsSortInput[] = [
  { field: 'date', direction: 'desc' },
  { field: 'createdAt', direction: 'desc' },
]

type UseContentsListOptions = {
  perPage?: number
  sort?: ContentsSortInput[]
  channelIds?: string[]
  includeChannellessContent?: boolean
  tagIds?: string[]
  /** Omit for every status — the whole point of `contents` over `drafts`. */
  statuses?: PostStatus[]
  /** Sent as `filter.date`'s `range` — the caller resolves day boundaries. */
  date?: ContentsDateRange
}

type UseContentsListResult = {
  data: GetContentsListData | undefined
  loading: boolean
  fetchingMore: boolean
  reloading: boolean
  error: UseQuery.Result<GetContentsListData, GetContentsListVariables>['error']
  lastElementRef: UseIntersectionObserverReturn[0]
  refetch: UseQuery.Result<
    GetContentsListData,
    GetContentsListVariables
  >['refetch']
}

/**
 * Paginated list of an organization's content, across every post status, via
 * the `contents` query — the same shape `useDraftsList` gives the Draft
 * column, but without `drafts`' hardcoded draft-only status filter.
 */
export function useContentsList({
  perPage = CONTENTS_PER_PAGE,
  sort = DEFAULT_SORT,
  channelIds,
  includeChannellessContent = false,
  tagIds,
  statuses,
  date,
}: UseContentsListOptions = {}): UseContentsListResult {
  const organizationId = useOrganizationId()

  // Omitting the filter entirely already returns every channel's posts
  // alongside channel-less content — sending `channelIds: []` instead means
  // "channel-less only" (see the schema doc on ContentsChannelFilter), so
  // only build the filter once a real channel selection narrows it.
  const channel =
    channelIds !== undefined
      ? { channelIds, includeChannellessContent }
      : undefined
  const tags = buildTagComparator(tagIds)
  const dateFilter = date ? { range: date } : undefined

  const { data, refetch, fetchMore, error, networkStatus } = useQuery(
    GetContentsList,
    {
      variables: {
        first: perPage,
        after: null,
        organizationId: organizationId || '',
        sort,
        channel,
        tags,
        status: statuses?.length ? statuses : undefined,
        date: dateFilter,
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      errorPolicy: 'all',
      skip: !organizationId,
    },
  )

  const loading = networkStatus === NetworkStatus.loading && !data?.contents
  const fetchingMore = networkStatus === NetworkStatus.fetchMore
  const reloading =
    networkStatus === NetworkStatus.setVariables && !data?.contents
  const endCursor = data?.contents.pageInfo.endCursor

  // The server decides where a changed row lands in the merged sort, so the
  // list refetches instead of placing the row itself, as `useDraftsList` does.
  const scheduleRefetch = useDebouncedTask(refetch, 'contents list refetch')
  usePusherEvent(CONTENT_ITEM_EVENTS, (): void => {
    if (channel && !channel.includeChannellessContent) return
    scheduleRefetch()
  })

  const isPostChannelInView = (channelId?: string): boolean =>
    !channel || (!!channelId && channel.channelIds.includes(channelId))
  const isPostLoaded = (postId?: string): boolean =>
    !!postId &&
    !!data?.contents.edges.some((edge) => getContentEdgeId(edge) === postId)

  usePusherEvent(POST_EVENTS, (event: PusherPostEventData): void => {
    if (!isPostChannelInView(event.profile_id)) return
    scheduleRefetch()
  })
  const isStatusOrDateFiltered = !!statuses?.length || !!date
  usePusherEvent(POST_STATUS_EVENTS, (event: PusherPostEventData): void => {
    const canJoinList =
      isStatusOrDateFiltered && isPostChannelInView(event.profile_id)
    if (!canJoinList && !isPostLoaded(event.update_id ?? event.draft_id)) {
      return
    }
    scheduleRefetch()
  })
  usePusherEvent(POST_REMOVED_EVENTS, (event: PusherPostEventData): void => {
    if (!isPostLoaded(event.update_id ?? event.draft_id)) return
    scheduleRefetch()
  })

  const [lastElementRef] = useInfiniteScrollPagination({
    loading: fetchingMore,
    hasNextPage: data?.contents.pageInfo.hasNextPage,
    fetchMore: async () => {
      try {
        await fetchMore({
          variables: { after: endCursor },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult?.contents?.edges) return prev

            const newEdges = fetchMoreResult.contents.edges
            const newIds = new Set(newEdges.map(getContentEdgeId))
            const existingEdges = (prev.contents?.edges ?? []).filter(
              (edge) => !newIds.has(getContentEdgeId(edge)),
            )

            return {
              ...prev,
              contents: {
                ...prev.contents,
                edges: [...existingEdges, ...newEdges],
                pageInfo: fetchMoreResult.contents.pageInfo,
                totalCount: fetchMoreResult.contents.totalCount,
              },
            }
          },
        })
      } catch (caughtError) {
        const message =
          caughtError instanceof Error ? caughtError.message : 'Unknown error'

        toast.error('Error fetching more content')
        throw new Error(`Failed to fetch more content: ${message}`, {
          cause: caughtError,
        })
      }
    },
  })

  return useMemo(
    () => ({
      data,
      loading,
      fetchingMore,
      reloading,
      error,
      lastElementRef,
      refetch,
    }),
    [data, loading, fetchingMore, reloading, error, lastElementRef, refetch],
  )
}
